import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { api, type Json } from './api';

export type Role = 'admin' | 'approver' | 'accountant' | 'viewer';
type Me = { signed_in: boolean; email?: string; roles?: Role[]; settings?: Json; fy?: Json };

type Ctx = {
  session: Session | null;
  loading: boolean;
  me: Me | null;
  roles: Role[];
  settings: Json;
  fy: Json | null;
  hasRole: (...roles: Role[]) => boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};
const AuthCtx = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const qc = useQueryClient();
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setReady(true); });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => { setSession(s); qc.invalidateQueries({ queryKey: ['me'] }); });
    return () => sub.subscription.unsubscribe();
  }, [qc]);
  const meQ = useQuery({ queryKey: ['me', session?.user?.id ?? 'anon'], queryFn: () => api.me(), enabled: !!session, staleTime: 60_000 });
  const me = (meQ.data as Me | undefined) ?? null;
  const roles = useMemo(() => (me?.roles ?? []) as Role[], [me]);
  const hasRole = useCallback((...r: Role[]) => roles.includes('admin') || r.some((x) => roles.includes(x)), [roles]);
  const signOut = useCallback(async () => { await supabase.auth.signOut(); qc.clear(); }, [qc]);
  const refresh = useCallback(async () => { await qc.invalidateQueries({ queryKey: ['me'] }); }, [qc]);
  const value = useMemo<Ctx>(() => ({ session, loading: !ready || (!!session && meQ.isLoading), me, roles, settings: me?.settings ?? {}, fy: me?.fy ?? null, hasRole, signOut, refresh }),
    [session, ready, meQ.isLoading, me, roles, hasRole, signOut, refresh]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const c = useContext(AuthCtx);
  if (!c) throw new Error('useAuth outside AuthProvider');
  return c;
}
